import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X } from 'lucide-react';
import { ADMIN_FORM_MODAL_PANEL } from './adminModalPanel';
import { formatCycleDate, cyclesForVigenciaAssign, pickVigenciaAssignCycleId } from '../../utils/cycleUtils';

const DAY_PRESETS = [7, 15, 30, 60, 90];

function cycleOptionLabel(c) {
  if (!c) return '';
  const start = formatCycleDate(c.startDate || c.start_date);
  const end = formatCycleDate(c.endDate || c.end_date);
  const range = start && end ? ` (${start} – ${end})` : (start ? ` (${start})` : '');
  return `${c.name || `Ciclo ${c.id}`}${range}`;
}

/**
 * Modal confirmar pago / resultado del plan adquirido (Vigencias, admin sede).
 * El admin elige vigencia en días y/o ciclo (referencia en reportes por ciclo).
 */
export default function ConfirmPaymentModal({
  open,
  phase = 'confirm', // confirm | success
  saving = false,
  userName = '',
  userEmail = '',
  plan = '',
  cycleId = null,
  cycles = [],
  estado = '',
  validUntil = null,
  deferred = false,
  daysGranted = null,
  message = null,
  observaciones = '',
  onObservacionesChange,
  onClose,
  onConfirm,
  activateMode = false, // admin sede: copy «Activar cliente»
  renewMode = false, // cliente antiguo: copy «Renovar»
  defaultAmount = null, // precio plan / cobrado editable (premio, excedente)
  planBaseAmount = null,
  premioLabel = null,
  t = (k, fb) => fb || k,
}) {
  const [days, setDays] = useState('30');
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (!open) return;
    setDays('30');
    setSelectedCycleId(pickVigenciaAssignCycleId(cycles, cycleId));
    const seedAmt = defaultAmount != null && defaultAmount !== ''
      ? defaultAmount
      : (planBaseAmount != null ? planBaseAmount : '');
    setAmount(seedAmt === '' || seedAmt == null ? '' : String(seedAmt));
  }, [open, cycleId, defaultAmount, planBaseAmount, cycles]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return null;
    return d.toLocaleDateString();
  };

  const parsedDays = Number(days);
  const validDays = Number.isFinite(parsedDays) && parsedDays >= 1 && parsedDays <= 365;
  const parsedAmount = amount === '' ? null : Math.round(Number(amount));
  const validAmount = parsedAmount == null || (Number.isFinite(parsedAmount) && parsedAmount >= 0);
  const baseNum = planBaseAmount != null && planBaseAmount !== '' ? Number(planBaseAmount) : null;
  const excedente = parsedAmount != null && Number.isFinite(baseNum)
    ? parsedAmount - baseNum
    : null;
  const cycleOptions = cyclesForVigenciaAssign(cycles);
  const selectedCycle = cycleOptions.find((c) => String(c.id) === String(selectedCycleId));
  const cycleLabel = selectedCycle ? cycleOptionLabel(selectedCycle) : null;

  const handleConfirm = () => {
    if (!validDays || !validAmount || saving) return;
    const cycleInCatalog = selectedCycle
      ? (Number(selectedCycle.id) || selectedCycle.id)
      : null;
    onConfirm?.({
      days: parsedDays,
      cycle_id: cycleInCatalog,
      force_enable: true,
      ...(parsedAmount != null ? { monto_cobrado: parsedAmount, precio_mensual: parsedAmount } : {}),
    });
  };

  const modal = (
    <div
      className="form-modal-overlay"
      role="presentation"
      onClick={() => !saving && onClose?.()}
    >
      <div
        className={`${ADMIN_FORM_MODAL_PANEL} max-w-lg`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-payment-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 id="confirm-payment-title" className="text-lg font-bold text-stone-900 m-0">
              {phase === 'success'
                ? t('vigencias.plan_acquired_title', 'Usuario activado')
                : (activateMode
                  ? t('vigencias.activate_client', 'Activar cliente')
                  : renewMode
                    ? t('vigencias.renew_modal_title', 'Renovar')
                    : t('vigencias.confirm_modal_title', 'Confirmar pago'))}
            </h3>
            <p className="text-sm text-stone-600 mt-1 mb-0">
              {phase === 'success'
                ? t(
                  'vigencias.plan_acquired_hint',
                  'El pago quedó confirmado. El usuario queda activo con la vigencia indicada.',
                )
                : t(
                  'vigencias.confirm_modal_hint',
                  'Elige por cuántos días queda activo y, si aplica, el ciclo (aparece en reportes por ciclo).',
                )}
            </p>
          </div>
          <button type="button" className="btn-secondary p-2" disabled={saving} onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 text-sm text-stone-800">
          {userName ? (
            <p className="m-0"><strong>{t('vigencias.col_user', 'Usuario')}:</strong> {userName}</p>
          ) : null}
          {userEmail ? (
            <p className="m-0 text-stone-600">{userEmail}</p>
          ) : null}
          <p className="m-0">
            <strong>{t('vigencias.col_plan', 'Plan')}:</strong>{' '}
            {plan || t('vigencias.plan_unknown', 'Sin plan indicado')}
          </p>
          {estado ? (
            <p className="m-0"><strong>{t('vigencias.col_status', 'Estado')}:</strong> {estado}</p>
          ) : null}
          {premioLabel ? (
            <p className="m-0 text-amber-800 font-semibold">
              {t('vigencias.premio_badge', 'Premio')}: {premioLabel}
            </p>
          ) : null}

          {phase === 'confirm' ? (
            <>
              <div>
                <label className="label" htmlFor="confirm-amount">
                  {t('vigencias.amount_charged', 'Monto cobrado (COP)')}
                </label>
                <input
                  id="confirm-amount"
                  type="number"
                  min={0}
                  step={1000}
                  className="input w-full mt-1"
                  value={amount}
                  disabled={saving}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={t('vigencias.amount_ph', 'Ej. 700000')}
                />
                <p className="text-xs text-stone-500 mt-1 mb-0">
                  {Number.isFinite(baseNum)
                    ? t(
                      'vigencias.amount_hint_plan',
                      'Precio del plan: ${n}. Puedes subir el monto si hay premio/excedente.',
                    ).replace('{n}', Number(baseNum).toLocaleString('es-CO'))
                    : t(
                      'vigencias.amount_hint',
                      'Editable. Si hay premio o excedente, deja la diferencia registrada en observaciones.',
                    )}
                  {excedente != null && excedente !== 0 ? (
                    <span className="block text-amber-800 mt-0.5">
                      {t('vigencias.excedente', 'Excedente')}: ${Number(excedente).toLocaleString('es-CO')} COP
                    </span>
                  ) : null}
                </p>
              </div>

              <div>
                <label className="label" htmlFor="confirm-days-presets">
                  {t('vigencias.days_active', 'Días activo')}
                </label>
                <div id="confirm-days-presets" className="flex flex-wrap gap-2 mt-1">
                  {DAY_PRESETS.map((preset) => {
                    const selected = days === String(preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        disabled={saving}
                        className={`min-w-[3rem] px-3 py-1.5 rounded-xl text-sm font-medium ${
                          selected ? 'btn-primary' : 'btn-secondary'
                        }`}
                        onClick={() => setDays(String(preset))}
                      >
                        {preset}d
                      </button>
                    );
                  })}
                </div>
                <input
                  type="number"
                  min={1}
                  max={365}
                  className="input w-full mt-2"
                  value={days}
                  disabled={saving}
                  onChange={(e) => setDays(e.target.value)}
                  placeholder={t('vigencias.days_placeholder', 'Ej. 30')}
                />
                <p className="text-xs text-stone-500 mt-1 mb-0">
                  {t('vigencias.confirm_days_hint', 'Entre 1 y 365. Define hasta cuándo queda activo el usuario.')}
                </p>
              </div>

              <div>
                <label className="label" htmlFor="confirm-cycle-select">
                  {t('vigencias.assign_cycle', 'Asignar ciclo (reportes)')}
                </label>
                <select
                  id="confirm-cycle-select"
                  className="input w-full mt-1"
                  value={selectedCycleId}
                  disabled={saving}
                  onChange={(e) => setSelectedCycleId(e.target.value)}
                >
                  <option value="">{t('vigencias.cycle_optional', 'Sin ciclo / usar el del plan')}</option>
                  {(cycleOptions || []).map((c) => (
                    <option key={`${c.id}-${c.gym_id ?? 'plat'}`} value={String(c.id)}>{cycleOptionLabel(c)}</option>
                  ))}
                </select>
                <p className="text-xs text-stone-500 mt-1 mb-0">
                  {t(
                    'vigencias.confirm_cycle_hint',
                    'Si eliges un ciclo, quedará referenciado en el reporte por ciclos.',
                  )}
                </p>
              </div>

              <label className="block">
                <span className="font-semibold text-stone-800">{t('vigencias.col_notes', 'Observaciones')}</span>
                <textarea
                  className="input mt-1 w-full min-h-[72px] text-sm"
                  value={observaciones || ''}
                  disabled={saving}
                  onChange={(e) => onObservacionesChange?.(e.target.value)}
                  placeholder={t('vigencias.notes_ph', 'Nota al confirmar el pago (opcional)…')}
                />
              </label>
            </>
          ) : (
            <>
              {daysGranted != null ? (
                <p className="m-0">
                  <strong>{t('vigencias.days_active', 'Días activo')}:</strong> {daysGranted}
                </p>
              ) : null}
              {cycleLabel ? (
                <p className="m-0"><strong>{t('vigencias.col_cycle', 'Ciclo')}:</strong> {cycleLabel}</p>
              ) : null}
              {validUntil ? (
                <p className="m-0">
                  <strong>{t('vigencias.col_expiry', 'Vence')}:</strong> {formatDate(validUntil)}
                </p>
              ) : null}
              {deferred ? (
                <p className="m-0 text-amber-800">
                  {message || t('vigencias.confirm_success_deferred', 'Pago registrado. El acceso se habilitará al iniciar el ciclo.')}
                </p>
              ) : (
                <p className="m-0 text-emerald-800 font-semibold flex items-start gap-2">
                  <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                  <span>
                    {message
                      || t(
                        'vigencias.confirm_success_active',
                        'Usuario activo. Vigencia aplicada correctamente.',
                      )}
                  </span>
                </p>
              )}
              {observaciones ? (
                <p className="m-0"><strong>{t('vigencias.col_notes', 'Observaciones')}:</strong> {observaciones}</p>
              ) : null}
            </>
          )}
        </div>

        <div className="form-modal-footer -mx-5 -mb-5 mt-4">
          {phase === 'confirm' ? (
            <>
              <button type="button" className="btn-secondary" disabled={saving} onClick={onClose}>
                {t('common.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-2"
                disabled={saving || !validDays || !validAmount}
                onClick={handleConfirm}
              >
                <CheckCircle2 size={16} />
                {saving
                  ? t('common.saving', 'Guardando...')
                  : (activateMode
                    ? t('vigencias.activate_with_days', 'Activar cliente {n} días').replace(
                      '{n}',
                      String(validDays ? parsedDays : '—'),
                    )
                    : renewMode
                      ? t('vigencias.renew_with_days', 'Renovar {n} días').replace(
                        '{n}',
                        String(validDays ? parsedDays : '—'),
                      )
                      : t('vigencias.confirm_with_days', 'Confirmar y activar {n} días').replace(
                        '{n}',
                        String(validDays ? parsedDays : '—'),
                      ))}
              </button>
            </>
          ) : (
            <button type="button" className="btn-primary" onClick={onClose}>
              {t('common.close', 'Cerrar')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}
